import { ModuleColumnDefinition } from "./module";
import { FieldTypeEnum } from "./custom-field.types";
import { ModuleTypeEnum } from "./module";

/**
 * Standard columns for the task module
 */
export const TASK_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: "taskNumber",
    name: "taskNumber",
    label: "Task #",
    description: "Task number",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 5,
    fieldType: "STRING" as const,
    width: "110px",
    minWidth: "80px",
  },
  {
    id: "name",
    name: "name",
    label: "Name",
    description: "The name of the task",
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 10,
    fieldType: "STRING" as const,
    width: "250px",
    minWidth: "200px",
  },
  {
    id: "description",
    name: "description",
    label: "Description",
    description: "Task description",
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 20,
    fieldType: "RICH_TEXT" as const,
    width: "220px",
    minWidth: "180px",
  },
  {
    id: "status",
    name: "status",
    label: "Status",
    description: "Current status of the task (resolved from hierarchy)",
    isVisible: false, // Hidden in table UI - statusTypeId is shown instead
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 29,
    fieldType: "STRING" as const,
    width: "150px",
    minWidth: "120px",
  },
  {
    id: "statusTypeId",
    name: "statusTypeId",
    label: "Status",
    description: "Current status of the task",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 30,
    fieldType: "STRING" as const,
    width: "150px",
    minWidth: "120px",
  },
  {
    id: "priority",
    name: "priority",
    label: "Priority",
    description: "Task priority level",
    isVisible: true,
    isRequired: false, // Has default value "MEDIUM" in database
    isSystem: true,
    isFixed: false,
    order: 40,
    fieldType: "STRING" as const,
    width: "140px",
    minWidth: "120px",
  },
  {
    id: "scheduledFor",
    name: "scheduledFor",
    label: "Scheduled Date",
    description: "Date when the task is scheduled to be worked",
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 45,
    fieldType: "DATE" as const,
    width: "160px",
    minWidth: "140px",
  },
  {
    id: "dueDate",
    name: "dueDate",
    label: "Due Date",
    description: "Date when the task is due",
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 50,
    fieldType: "DATE" as const,
    width: "160px",
    minWidth: "140px",
  },
  {
    id: "comments",
    name: "comments",
    label: "Comments",
    description: "Number of comments",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 57,
    fieldType: "STRING" as const,
    width: "80px",
    minWidth: "80px",
  },
  {
    id: "dependencies",
    name: "dependencies",
    label: "Dependencies",
    description: "Task dependencies",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 79,
    fieldType: "STRING" as const,
    width: "80px",
    minWidth: "30px",
  },
  {
    id: "recurring",
    name: "recurring",
    label: "Recurring",
    description: "Recurring pattern and status",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 55,
    fieldType: "STRING" as const,
    width: "80px",
    minWidth: "30px",
  },
  {
    id: "assignees",
    name: "assignees",
    label: "Assignees",
    description: "Users assigned to the task",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 60,
    fieldType: "STRING" as const,
    width: "180px",
    minWidth: "150px",
  },
  {
    id: "tags",
    name: "tags",
    label: "Tags",
    description: "Tags for organizing and categorizing the task",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 61,
    fieldType: "STRING" as const,
    width: "200px",
    minWidth: "150px",
  },
  {
    id: "deployment",
    name: "deployment",
    label: "Deployment",
    description: "Deployments this task is included in",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 62,
    fieldType: "STRING" as const,
    width: "200px",
    minWidth: "150px",
  },
  {
    id: "sprint",
    name: "sprint",
    label: "Sprint",
    description: "The sprint this task belongs to",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 64,
    fieldType: "STRING" as const,
    width: "160px",
    minWidth: "140px",
  },
  {
    id: "group",
    name: "group",
    label: "Task Group",
    description: "The group this task belongs to",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 65,
    fieldType: "STRING" as const,
    width: "160px",
    minWidth: "140px",
  },
  {
    id: "bucketId",
    name: "bucketId",
    label: "Bucket",
    description: "The bucket this task belongs to",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 66,
    fieldType: "STRING" as const,
    width: "180px",
    minWidth: "140px",
  },
  {
    id: "project",
    name: "project",
    label: "Project",
    description: "The project this task belongs to",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 68,
    fieldType: "STRING" as const,
    width: "200px",
    minWidth: "150px",
  },
  {
    id: "entity",
    name: "entity", 
    label: "Entity",
    description: "The entity this task is related to",
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 69,
    fieldType: "STRING" as const,
    width: "200px",
    minWidth: "150px",
  },
  {
    id: "moduleType",
    name: "moduleType",
    label: "Related To",
    description: "The type of module this task is related to",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 70,
    fieldType: "STRING" as const,
    width: "150px",
    minWidth: "120px",
  },
  {
    id: "storyPoints",
    name: "storyPoints",
    label: "Story Points",
    description: "Estimated story points for the task",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 75,
    fieldType: "NUMBER" as const,
    width: "120px",
    minWidth: "100px",
  },
  {
    id: "estimatedHours",
    name: "estimatedHours",
    label: "Estimated Hours",
    description: "Estimated hours to complete the task",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 76,
    fieldType: "NUMBER" as const,
    width: "140px",
    minWidth: "120px",
  },
  {
    id: "completedAt",
    name: "completedAt",
    label: "Completed",
    description: "Date when the task was completed",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 77,
    fieldType: "DATE" as const,
    width: "150px",
    minWidth: "130px",
  },
  {
    id: "isArchived",
    name: "isArchived",
    label: "Archived",
    description: "Whether the task is archived",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 78,
    fieldType: "BOOLEAN" as const,
    width: "100px",
    minWidth: "80px",
  },
  {
    id: "createdBy",
    name: "createdBy",
    label: "Created By",
    description: "User who created this task",
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 79.5,
    fieldType: "USER" as const,
    width: "160px",
    minWidth: "140px",
  },
  {
    id: "createdAt",
    name: "createdAt",
    label: "Created",
    description: "Date when the task was created",
    isVisible: false,
    isRequired: false, // Auto-generated by database
    isSystem: true,
    isFixed: false,
    order: 80,
    fieldType: "DATE" as const,
    width: "150px",
    minWidth: "130px",
  },
  {
    id: "updatedAt",
    name: "updatedAt",
    label: "Updated",
    description: "Date when the task was last updated",
    isVisible: false,
    isRequired: false, // Auto-generated by database
    isSystem: true,
    isFixed: false,
    order: 90,
    fieldType: "DATE" as const,
    width: "150px",
    minWidth: "130px",
  }
]; 

/**
 * Helper function to get the module type for tasks
 */
export function getTaskModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.TASK;
}

/**
 * Helper function to combine standard columns with custom fields and apply visibility preferences
 */
export function getTaskColumns(
  customFields: any[] = [], 
  preferences?: Record<string, boolean> | any
): ModuleColumnDefinition[] {
  // Extract column visibility preferences
  let columnVisibility: Record<string, boolean> = {};
  
  if (preferences) {
    if (preferences.columnPreferences) {
      // It's the full ColumnVisibilityPreference object
      columnVisibility = preferences.columnPreferences;
    } else if (typeof preferences === 'object') {
      // It's a simple Record<string, boolean>
      columnVisibility = preferences as Record<string, boolean>;
    }
  }
  
  // Extract column order preferences
  let columnOrders: Record<string, number> = {};
  
  if (preferences && preferences.columnOrders) {
    columnOrders = preferences.columnOrders;
  }
  
  // Apply preferences to standard columns
  const standardColumns = TASK_STANDARD_COLUMNS.map((column: ModuleColumnDefinition) => {
    // Check if there's a custom order for this column
    const orderKey = `${column.id}_order`;
    const customOrder = columnOrders[orderKey];
    
    // Check if we have a visibility preference for this column
    const hasVisibilityPref = columnVisibility[column.id] !== undefined;
    const visibilityValue = hasVisibilityPref ? !!columnVisibility[column.id] : column.isVisible;

    return {
      ...column,
      // Override visibility if preferences exist
      isVisible: visibilityValue,
      // Apply custom order if available
      order: customOrder !== undefined ? customOrder : column.order
    };
  });
  
  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const columnId = `custom_${field.fieldName}`;
    const orderKey = `${columnId}_order`;
    const customOrder = columnOrders[orderKey];
    
    // Check if we have a visibility preference for this column
    const hasVisibilityPref = columnVisibility[columnId] !== undefined;
    const visibilityValue = hasVisibilityPref ? !!columnVisibility[columnId] : !!field.isActive;
    
    // Parse dropdown options from settings if available
    const fieldOptions = (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT')
      && field.settings?.options && Array.isArray(field.settings.options)
      ? field.settings.options.map((opt: any) => {
          if (typeof opt === 'string') return { value: opt, label: opt };
          return { value: String(opt.value || opt.id || opt.label || ''), label: String(opt.label || opt.name || opt.value || '') };
        })
      : undefined;

    return {
      id: columnId,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityValue,
      isRequired: false,
      isSystem: false,
      // Apply custom order if available, otherwise use default ordering (after tags at 90)
      order: customOrder !== undefined ? customOrder : (91 + index),
      fieldType: field.fieldType,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    };
  }) as ModuleColumnDefinition[];
  
  // Combine both types of columns
  return [...standardColumns, ...customColumns];
}
